"use client"

import { useEffect } from "react"
import { useAuth } from "@/features/auth/hooks/use-auth"
import { useLocalizedRouter } from "@/shared/hooks/use-localized-router"
import {
  getPostHogForActivationSprint,
  normalizeActivationSprintVariant,
  ACTIVATION_SPRINT_FLAG,
} from "@/features/activation-sprint/lib/activation-sprint-experiment"

/**
 * Detects fresh-signup cookie set by /api/auth/callback and — once PostHog
 * flags load — routes activation-sprint variant users to /create.
 * PostHog rollout is 0% until CRO/ndong approve the experiment design.
 */
export function ActivationSprintRouter() {
  const router = useLocalizedRouter()
  const { user, isLoading } = useAuth()

  useEffect(() => {
    if (isLoading || !user) return
    const userId = user.id

    const hasCookie =
      typeof document !== "undefined" &&
      document.cookie.split(";").some((c) => c.trim().startsWith("activation_sprint_signup=1"))

    if (!hasCookie) return

    const ph = getPostHogForActivationSprint()
    if (!ph) return
    const posthog = ph

    let cancelled = false
    let unsubscribe: (() => void) | void

    function tryRoute(): boolean {
      const rawVariant = posthog.getFeatureFlag(ACTIVATION_SPRINT_FLAG)
      if (rawVariant === undefined) return false

      const distinctId = posthog.get_distinct_id?.()
      if (distinctId && distinctId !== userId) return false

      // Clear the cookie only after an identified-user flag assignment resolves.
      document.cookie = "activation_sprint_signup=; Max-Age=0; path=/"

      const variant = normalizeActivationSprintVariant(rawVariant)
      if (variant === "activation") {
        router.replace("/create?activation_sprint=1")
      }
      // control and unassigned: stay on home page, no redirect
      return true
    }

    function resolveWhenReady() {
      if (cancelled) return
      const resolved = tryRoute()
      if (resolved && typeof unsubscribe === "function") {
        unsubscribe()
        unsubscribe = undefined
      }
    }

    // PostHog flags may not be loaded yet — wait for them if needed
    if (posthog.onFeatureFlags) {
      unsubscribe = posthog.onFeatureFlags(resolveWhenReady)
      resolveWhenReady()
      return () => {
        cancelled = true
        if (typeof unsubscribe === "function") unsubscribe()
      }
    }

    resolveWhenReady()
  }, [user, isLoading, router])

  return null
}
